import axios, { AxiosResponse } from 'axios';
import type {
  Activity,
  ActivityWrite,
  ApiTokenRow,
  NewApiToken,
  BillingRate,
  BillingRateWrite,
  Budget,
  BudgetWrite,
  AuthResponse,
  AuthUser,
  BosSsoConfig,
  ChangePasswordResponse,
  DashboardStats,
  Document,
  InviteInfo,
  Issue,
  IssueWrite,
  Member,
  MemberInviteResult,
  MemberWrite,
  Notification,
  Phase,
  PhaseWrite,
  PortalSettings,
  PortalUser,
  Project,
  ContributorReport,
  FlowMetrics,
  ProjectFinancials,
  ProjectWrite,
  ReviewState,
  RunningTimer,
  Subtask,
  SubtaskWrite,
  Task,
  TaskWrite,
  TimeLog,
  TimeLogWrite,
  TokenPair,
} from './types';
import { dedupeGet } from './dedupe';
import { clearSession, refreshAccessToken, resetRefreshState } from './session';
import { loginUrlAfterSessionExpired } from '../components/auth/returnPath';
import * as v2 from './v2adapter';
import type { components } from './schema.v2';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  try {
    const tokens = localStorage.getItem('auth_tokens');
    if (tokens) {
      const { access } = JSON.parse(tokens);
      if (access) config.headers.Authorization = `Bearer ${access}`;
    }
  } catch {
    // A corrupt or unreadable token store must not stop the request being sent
    // unauthenticated; the server will answer 401 and the flow below handles it.
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // `error.config` is absent when the request never got made (a network failure, an
    // aborted call). Reading `_retry` off it threw, and the throw replaced the real error
    // with a TypeError nobody could act on.
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const access = await refreshAccessToken(BASE_URL);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${access}`;
        return api(original);
      } catch {
        // The refresh itself failed — the session really is over. `session.ts` explains why
        // this path used to be reached every single time in production.
        clearSession();
        resetRefreshState();
        // Only from inside the app. Doing this on the login page itself produced a reload
        // loop when a stale token was still in storage. The page they were on goes with
        // them as ?next=: a full page load carries no router state, and a bare /login lost
        // the task an expired session had just clicked through to from an email.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = loginUrlAfterSessionExpired(window.location);
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Every request and response below is typed from the backend's OpenAPI schema
 * (see ./types.ts). These signatures were `any`, which meant a field renamed on the
 * server produced no compile error anywhere in the app — the failure only appeared at
 * runtime as undefined data. Regenerate with `npm run generate:api` after changing the
 * API; `npm run check:api` fails the build if the checked-in types are stale.
 */
type Res<T> = Promise<AxiosResponse<T>>;
export function getActiveOrgSlug(): string {
  try {
    const userStr = localStorage.getItem('auth_user');

    if (userStr) {
      const user = JSON.parse(userStr);

      if (user?.portal_name) {
        return user.portal_name;
      }

      const firstPortal = user?.available_portals?.[0];

      if (firstPortal?.portal) {
        return firstPortal.portal;
      }
    }

    return '';
  } catch {
    return '';
  }
}

export interface RegisterPayload {
  email: string;
  full_name?: string;
  password: string;
  confirm_password: string;
  invite_token?: string;
}

export const authAPI = {
  register: (data: RegisterPayload): Res<AuthResponse> =>
    api.post('/api/auth/register/', data),
  login: (data: { email: string; password: string }): Res<AuthResponse> =>
    api.post('/api/auth/login/', data),
  /**
   * Redeem an invitation as an account that already exists.
   *
   * Registration refuses a duplicate email, so this is the only route for somebody who had
   * signed up before being invited — which, once a workspace has any members, is most
   * people. Requires the caller to be signed in AS the invited address; the server refuses
   * a mismatch, because the token travels by email and is not secret.
   */
  /** Ask for a reset link. Answers the same either way — never branch the UI on it. */
  requestPasswordReset: (email: string): Res<{ detail: string }> =>
    api.post('/api/auth/password-reset/', { email }),

  /** Spend a reset link. The token is single-use: it stops verifying once the password changes. */
  confirmPasswordReset: (
    token: string, password: string, confirm_password: string,
  ): Res<{ detail: string }> =>
    api.post('/api/auth/password-reset/confirm/', { token, password, confirm_password }),

  acceptInvite: (token: string): Res<{ portal: string; portalName: string; role: string }> =>
    api.post('/api/auth/accept-invite/', { invite_token: token }),

  inviteInfo: (token: string): Res<InviteInfo> =>
    api.get('/api/auth/invite-info/', { params: { token } }),
  me: (): Res<AuthUser> => api.get('/api/auth/me/'),
  /** Your own name, as everyone sees it. */
  updateMe: (data: { full_name: string }): Res<AuthUser> => api.patch('/api/auth/me/', data),
  switchPortal: (portal: string): Res<{ user: AuthUser }> =>
    api.post('/api/auth/switch-portal/', { portal }),
  logout: (refresh: string): Res<{ message: string }> =>
    api.post('/api/auth/logout/', { refresh }),
  changePassword: (data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Res<ChangePasswordResponse> => api.post('/api/auth/change-password/', data),
  refreshToken: (refresh: string): Res<TokenPair> =>
    api.post('/api/auth/token/refresh/', { refresh }),
  // Fetched independently from five places.
  portalUsers: (): Res<PortalUser[]> =>
    dedupeGet('portal-users', () => api.get('/api/auth/portal-users/')),
  /**
   * Whether the server offers "Sign in with BOS". False whenever single sign-on is off (the
   * default) or only partly configured, so the login page shows the button only on true.
   */
  bosConfig: (): Res<BosSsoConfig> => api.get('/api/auth/bos/config/'),
  /**
   * Trade the one-time code the BOS sign-in redirect carried for the same response password
   * sign-in returns. The code works once, for two minutes.
   */
  bosExchange: (code: string): Res<AuthResponse> =>
    api.post('/api/auth/bos/exchange/', { code }),
};

/**
 * Where "Sign in with BOS" starts: a full-page navigation to the backend, which redirects to
 * the BOS identity provider and, once the person has signed in there, back to
 * `/auth/bos/complete`. Not an XHR — the provider's sign-in page has to be shown.
 *
 * `next` survives the round trip (the server keeps only a path inside the portal), so a deep
 * link is not lost by signing in this way either.
 */
export function bosLoginUrl(next?: string | null): string {
  // REACT_APP_API_URL is "/" in production; joined naively that is "//api/…", which a
  // browser reads as a host called "api".
  const base = BASE_URL.replace(/\/+$/, '');
  const query = next ? `?next=${encodeURIComponent(next)}` : '';
  return `${base}/api/auth/bos/login/${query}`;
}

/**
 * The v1 surface, served from v2.
 *
 * These signatures and return shapes are unchanged — the pages still receive the Convex
 * document shapes they were written against — but every call now goes to
 * `/api/v2/orgs/<slug>/...` and Postgres. `backend/projects/` and its Convex proxy are
 * gone.
 *
 * `src/api/v2adapter.ts` does the translation and explains where the two APIs disagree.
 * Both files are transitional: as each page is ported to the native v2 shapes its entry
 * here stops being called and can go. Nothing new should be built against these shapes.
 */
// The adapter needs the configured axios instance (base URL, auth interceptor,
// refresh handling) but must not import this module back — hence injection.
v2.useRequester(api);

export const projectsAPI = {
  // Every page, not the first fifty. This feeds every project picker and the id->name map
  // the Workload and Performance pages resolve against; a truncated read silently drops
  // real options and leaves rows labelled with a bare key.
  list: (): Res<Project[]> =>
    dedupeGet('projects', () => v2.fetchAllPages('projects/?page_size=200', v2.asProject)),
  create: (data: ProjectWrite): Res<Project> =>
    // projectWrite is async — it resolves the owner email to a member id — so the body is
    // built first, then posted. Not an `async` arrow: annotating one with the `Res` alias
    // trips TS1055 under this target, and the .then chain keeps the alias.
    v2.projectWrite(data).then((body) =>
      api.post(v2.orgUrl('projects/'), body)
        .then((r) => ({ ...r, data: v2.asProject(r.data) }))),
  get: (id: string): Res<Project> =>
    api.get(v2.orgUrl(`projects/${id}/`)).then((r) => ({ ...r, data: v2.asProject(r.data) })),
  update: (id: string, data: ProjectWrite): Res<Project> =>
    // PATCH, not PUT: v2 write serializers are strict and a PUT would demand every field.
    v2.projectWrite(data).then((body) =>
      api.patch(v2.orgUrl(`projects/${id}/`), body)
        .then((r) => ({ ...r, data: v2.asProject(r.data) }))),
  delete: (id: string): Res<void> => api.delete(v2.orgUrl(`projects/${id}/`)),
  // Undo a delete — restores the project and the work items its delete cascade hid.
  restore: (id: string): Res<Project> =>
    api.post(v2.orgUrl(`projects/${encodeURIComponent(id)}/restore/`))
      .then((r) => ({ ...r, data: v2.asProject(r.data) })),

  dashboardStats: (): Res<DashboardStats> =>
    dedupeGet('dashboard-stats', async () => {
      const response = await api.get(v2.orgUrl('dashboard/'));
      return { ...response, data: v2.asDashboardStats(response.data) };
    }),

  myTasks: (): Res<Task[]> => dedupeGet('my-tasks', () =>
    v2.listWorkItems({ assignee: 'me' })),
  //: Work someone has asked YOU to review and you have not decided on yet.
  reviewsForMe: (): Res<Task[]> => dedupeGet('reviews-for-me', () =>
    v2.listWorkItems({ reviewer: 'me', review_state: 'pending' })),
  allTasks: (): Res<Task[]> => dedupeGet('all-tasks', () =>
    v2.listWorkItems({ type: 'task' })),
  allIssues: (): Res<Issue[]> => dedupeGet('all-issues', () =>
    v2.listWorkItems({ type: 'bug' }, v2.asIssue)) as unknown as Res<Issue[]>,
  portalSettings: {
    get: (): Res<PortalSettings> => api.get(v2.orgUrl('settings/')),
    update: (data: Partial<PortalSettings>): Res<PortalSettings> =>
      api.patch(v2.orgUrl('settings/'), data),
  },

  members: {
    // Every page. The Workload band counts people from this list and the Performance page
    // resolves names against it, so a fifty-row cap would quietly shrink the team.
    list: (): Res<Member[]> =>
      dedupeGet('members', () => v2.fetchAllPages('members/?page_size=200', v2.asMember)),
    /**
     * Invite someone.
     *
     * The response goes through `asMember` exactly as `update` does. It did not, so the
     * caller received the RAW v2 payload — whose address field is `invited_email`, not
     * `email` — and the freshly invited row rendered as the literal name "Member" with no
     * address at all, until a reload refetched the list through the adapter. You could not
     * see who you had just invited.
     *
     * `invitation_sent` and `has_account` are carried through on top: they are not member
     * fields, they are the outcome of this one call, and the invite message needs both.
     */
    create: (data: MemberWrite): Res<MemberInviteResult> =>
      api.post(v2.orgUrl('members/'), {
        invited_email: (data as { email?: string }).email,
        role: (data as { role?: string }).role,
      }).then((r) => ({
        ...r,
        data: {
          ...v2.asMember(r.data),
          invitation_sent: r.data?.invitation_sent,
          has_account: r.data?.has_account,
        } as MemberInviteResult,
      })),
    update: (email: string, data: MemberWrite): Res<Member> =>
      v2.withMemberId(email, (id) =>
        api.patch(v2.orgUrl(`members/${id}/`), { role: (data as { role?: string }).role })
          .then((r) => ({ ...r, data: v2.asMember(r.data) }))),
    delete: (email: string): Res<void> =>
      v2.withMemberId(email, (id) => api.delete(v2.orgUrl(`members/${id}/`))),
  },

  // Per-project teams: who is on a project and their role there.
  projectMembers: {
    list: (projectId: string): Res<v2.ProjectMember[]> => v2.listProjectMembers(projectId),
    add: (projectId: string, email: string, role: string): Res<v2.ProjectMember> =>
      v2.addProjectMember(projectId, email, role),
    updateRole: (id: number, role: string): Res<v2.ProjectMember> =>
      v2.updateProjectMemberRole(id, role),
    remove: (id: number): Res<void> => v2.removeProjectMember(id),
    restore: (id: number): Res<v2.ProjectMember> =>
      api.post(v2.orgUrl(`project-members/${id}/restore/`)),
    getVisibility: (projectId: string): Promise<v2.ProjectVisibility> =>
      v2.getProjectVisibility(projectId),
    setVisibility: (projectId: string, visibility: v2.ProjectVisibility): Res<unknown> =>
      v2.setProjectVisibility(projectId, visibility),
  },

  // The project's objective tree: Project -> Goal -> Module -> Task -> Subtask. Goals and
  // modules are trackable containers; tasks live inside a module via WorkItem.module.
  goals: {
    list: (projectId: string): Promise<v2.Goal[]> => v2.listGoals(projectId),
    create: (projectId: string, body: v2.GoalWrite): Promise<v2.Goal> =>
      v2.createGoal(projectId, body),
    update: (id: number, body: v2.GoalWrite): Promise<v2.Goal> => v2.updateGoal(id, body),
    remove: (id: number): Promise<void> => v2.deleteGoal(id),
    // Undo a delete — clears the soft delete server-side. MANAGE-gated, like remove.
    restore: (id: number): Res<v2.Goal> => api.post(v2.orgUrl(`goals/${id}/restore/`)),
  },
  modules: {
    list: (opts: { goalId?: number; projectKey?: string }): Promise<v2.Module[]> =>
      v2.listModules(opts),
    create: (goalId: number, body: Omit<v2.GoalWrite, 'start_on' | 'end_on'>): Promise<v2.Module> =>
      v2.createModule(goalId, body),
    update: (id: number, body: Omit<v2.GoalWrite, 'start_on' | 'end_on'>): Promise<v2.Module> =>
      v2.updateModule(id, body),
    remove: (id: number): Promise<void> => v2.deleteModule(id),
    restore: (id: number): Res<v2.Module> => api.post(v2.orgUrl(`modules/${id}/restore/`)),
  },

  notifications: {
    list: (): Res<Notification[]> => api.get(v2.orgUrl('notifications/'))
      .then((r) => ({ ...r, data: v2.unwrap<Record<string, any>>(r).data.map(v2.asNotification) })),
    markRead: (notificationId: string): Res<{ success?: boolean }> =>
      api.post(v2.orgUrl(`notifications/${notificationId}/read/`)),
    /**
     * How many are unread — the server's total, from one row's page. The bell used to learn
     * its number only from the panel's own list, so it showed nothing until opened, and
     * could only ever count the first fifty.
     */
    unreadCount: (): Promise<number> =>
      api.get(v2.orgUrl('notifications/?unread=1&page_size=1'))
        .then((r) => Number((r.data as { count?: number })?.count ?? 0)),
    /** Every unread one, in one request — not one per row the panel happened to load. */
    readAll: (): Res<{ marked_read: number }> => api.post(v2.orgUrl('notifications/read-all/')),
  },

  activities: {
    list: (projectId?: string): Res<Activity[]> =>
      api.get(v2.orgUrl(`activity/${projectId ? `?project=${encodeURIComponent(projectId)}` : ''}`))
        .then((r) => ({ ...r, data: v2.unwrap<Record<string, any>>(r).data.map(v2.asActivity) })),
    /**
     * One page of the feed, filtered by the SERVER.
     *
     * `list` above returns the first page and drops the envelope, which is all the Home
     * summary needs. The Activity page needs both halves of what the envelope carries:
     * whether there is more history behind this page, and filters that apply to all of it
     * rather than to the fifty rows already on screen — filtering client-side meant picking
     * a colleague showed their share of the last few hours and called it their activity.
     */
    page: (params: {
      project?: string; verb?: string; actor?: string;
      from?: string; to?: string; page?: number; pageSize?: number;
    } = {}): Promise<{ rows: Activity[]; hasMore: boolean; count: number }> => {
      const query = new URLSearchParams();
      if (params.project) query.set('project', params.project);
      if (params.verb) query.set('verb', params.verb);
      if (params.actor) query.set('actor', params.actor);
      // Calendar days, inclusive at both ends — the server compares them as days.
      if (params.from) query.set('from', params.from);
      if (params.to) query.set('to', params.to);
      if (params.page && params.page > 1) query.set('page', String(params.page));
      if (params.pageSize) query.set('page_size', String(params.pageSize));
      const qs = query.toString();
      return api.get(v2.orgUrl(`activity/${qs ? `?${qs}` : ''}`)).then((r) => {
        const body = r.data as { count?: number; next?: string | null; results?: unknown[] };
        return {
          rows: v2.unwrap<Record<string, any>>(r).data.map(v2.asActivity),
          hasMore: Boolean(body?.next),
          count: Number(body?.count ?? 0),
        };
      });
    },
    // v2 has no write path: the feed is an audit record, and it was being used as a chat
    // transport with direct messages in a table every member could read. Callers are being
    // removed with the Collaboration page; this refuses loudly rather than silently
    // dropping writes.
    create: (_data: ActivityWrite): Res<Activity> => {
      throw new Error(
        'The activity feed is read-only. It is an audit record, not a message transport — '
        + 'writing to it is what put direct messages in a workspace-readable table.',
      );
    },
  },

  timelogs: {
    /**
     * Every matching time entry, not the newest fifty.
     *
     * This read one page and stopped, so Timesheets, Approvals and Reports all showed
     * totals computed from at most fifty entries with nothing on screen saying the rest
     * had been dropped. A wrong total that looks authoritative is worse than an error.
     *
     * `since`/`until` filter on `spent_on` server-side (`until` is exclusive), so a period
     * view fetches its period rather than fetching everything and slicing in the browser.
     */
    list: (
      projectId?: string,
      params?: { since?: string; until?: string; membership?: string },
    ): Res<TimeLog[]> => {
      const query = new URLSearchParams({ page_size: '200' });
      if (projectId) query.set('project', projectId);
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value) query.set(key, String(value));
      });
      return v2.fetchAllPages(`time-entries/?${query.toString()}`, v2.asTimeLog);
    },
    create: async (data: TimeLogWrite): Promise<AxiosResponse<TimeLog>> => {
      const body = await v2.timeLogWrite(data);
      const response = await api.post(v2.orgUrl('time-entries/'), body);
      return { ...response, data: v2.asTimeLog(response.data) };
    },
    update: async (logId: string, data: TimeLogWrite): Promise<AxiosResponse<TimeLog>> => {
      const body = await v2.timeLogWrite(data);
      const response = await api.patch(v2.orgUrl(`time-entries/${logId}/`), body);
      return { ...response, data: v2.asTimeLog(response.data) };
    },
    // Approval is a command, not a field write: `approval_status` is server-owned, so a
    // PATCH carrying it does nothing (which is why Approve/Reject looked like they worked
    // and changed nothing). These are the endpoints that actually decide an entry.
    approve: (logId: string): Res<TimeLog> =>
      api.post(v2.orgUrl(`time-entries/${logId}/approve/`))
        .then((r) => ({ ...r, data: v2.asTimeLog(r.data) })),
    reject: (logId: string, reason?: string): Res<TimeLog> =>
      api.post(v2.orgUrl(`time-entries/${logId}/reject/`), reason ? { reason } : {})
        .then((r) => ({ ...r, data: v2.asTimeLog(r.data) })),

    // Automatic time capture: one running Start/Stop timer per member. Stopping it logs a
    // normal priced, pending TimeEntry through the same path a manual entry takes.
    timer: {
      current: async (): Promise<RunningTimer | null> => {
        const res = await api.get(v2.orgUrl('time-entries/timer/'));
        const running = res.data?.running;
        return running ? v2.asRunningTimer(running) : null;
      },
      start: async (taskKey: string): Promise<RunningTimer> => {
        const workItem = await v2.resolveWorkItemId(taskKey);
        const res = await api.post(v2.orgUrl('time-entries/timer/start/'), { work_item: workItem });
        return v2.asRunningTimer(res.data);
      },
      stop: async (): Promise<TimeLog> => {
        const res = await api.post(v2.orgUrl('time-entries/timer/stop/'));
        return v2.asTimeLog(res.data);
      },
    },
  },

  tasks: {
    list: (projectId: string): Res<Task[]> => v2.listWorkItems({ project: projectId }),
    // One task, by key. The task page reads this instead of listing the whole project.
    get: (_projectId: string, taskId: string): Res<Task> => v2.fetchWorkItem(taskId),
    /**
     * This task's own history — every recorded change, newest first.
     *
     * The events were already being written by the services that perform each action, and
     * the endpoint already filtered by `work_item`; nothing read it. So a task page showed
     * the current values and no way to find out how they got there — who moved it, when it
     * was reassigned, what the due date used to be.
     */
    history: (taskKey: string): Res<v2.TaskHistoryEntry[]> =>
      api.get(v2.orgUrl(`activity/?work_item=${encodeURIComponent(taskKey)}&page_size=50`))
        .then((r) => {
          const page = v2.unwrap<Record<string, any>>(r);
          return { ...page, data: page.data.map(v2.asTaskHistory) };
        }),
    create: (projectId: string, data: TaskWrite): Res<Task> =>
      v2.createWorkItem(projectId, data, 'task'),
    bulkCreate: (
      projectId: string,
      titles: string[],
      shared?: { assignee?: string; priority?: string },
    ): Res<Task[]> => v2.bulkCreateWorkItems(projectId, titles, shared),
    // CSV import: per-row assignee (by email), priority, severity, status, type, due date.
    importCsv: (
      projectId: string,
      rows: v2.ImportRow[],
    ): Res<v2.ImportResult> => v2.importWorkItems(projectId, rows),
    update: (_projectId: string, taskId: string, data: TaskWrite): Res<Task> =>
      v2.updateWorkItem(taskId, data),
    delete: (_projectId: string, taskId: string): Res<void> =>
      api.delete(v2.orgUrl(`work-items/${taskId}/`)),
    restore: (_projectId: string, taskId: string): Res<void> =>
      api.post(v2.orgUrl(`work-items/${encodeURIComponent(taskId)}/restore/`)),
    /**
     * Reassign, through v2's dedicated action rather than a field write.
     *
     * The action is not just a shortcut: it records the activity as `assigned` with the
     * affected member attached, and it writes the notification that tells the person they
     * have been given work. A PATCH of the same field records `updated` and notifies
     * nobody. Pass '' to unassign.
     */
    assign: (_projectId: string, taskId: string, email: string): Promise<void> =>
      v2.assignWorkItem(taskId, email),
    // Set the whole assignee set at once (multi-select). Empty list clears.
    assignMany: (_projectId: string, taskId: string, emails: string[]): Promise<void> =>
      v2.assignWorkItemMany(taskId, emails),
    // Submit the current user's own review decision (approve / request changes / pending).
    review: (_projectId: string, taskId: string, state: ReviewState): Promise<Task> =>
      v2.reviewWorkItem(taskId, state),
    // When done work was really finished — for work marked done after the day it was done.
    correctCompletion: (_projectId: string, taskId: string, completedAt: string): Promise<Task> =>
      v2.correctCompletion(taskId, completedAt),
    /**
     * Everything awaiting a scope decision, across every project.
     *
     * Approval is useless without a queue: until this existed the only way to find a pending
     * item was to open the task that happened to carry it, so proposed scope sat unagreed
     * and invisible.
     */
    pendingApproval: (): Res<Task[]> =>
      v2.listWorkItems({ approval_status: 'pending' }),
    /**
     * Sign off scope somebody proposed, so it starts counting towards the plan.
     *
     * Distinct from `review` above, which is a GitHub-style opinion on the WORK. This is a
     * decision about whether the work belongs in the plan at all, and only an Owner, an
     * Admin or the project's owner can make it.
     */
    approve: (_projectId: string, taskId: string): Res<Task> =>
      api.post(v2.orgUrl(`work-items/${encodeURIComponent(taskId)}/approve/`))
        .then((r) => ({ ...r, data: v2.asTask(r.data) })),
    rejectScope: (_projectId: string, taskId: string, reason: string): Res<Task> =>
      api.post(v2.orgUrl(`work-items/${encodeURIComponent(taskId)}/reject/`), { reason })
        .then((r) => ({ ...r, data: v2.asTask(r.data) })),
  },

  issues: {
    list: (projectId: string): Res<Issue[]> =>
      v2.listWorkItems({ project: projectId, type: 'bug' }, v2.asIssue) as unknown as Res<Issue[]>,
    create: (projectId: string, data: IssueWrite): Res<Issue> =>
      v2.createWorkItem(projectId, data as unknown as TaskWrite, 'bug') as unknown as Res<Issue>,
    update: (_projectId: string, issueId: string, data: IssueWrite): Res<Issue> =>
      v2.updateWorkItem(issueId, data as unknown as TaskWrite) as unknown as Res<Issue>,
    delete: (_projectId: string, issueId: string): Res<void> =>
      api.delete(v2.orgUrl(`work-items/${issueId}/`)),
    restore: (_projectId: string, issueId: string): Res<void> =>
      api.post(v2.orgUrl(`work-items/${encodeURIComponent(issueId)}/restore/`)),
  },
  phases: {
    list: (project?: string | number): Res<Phase[]> => {
      const params = project && project !== 'all' ? { project: String(project) } : {};
      return api.get(v2.orgUrl('phases/'), { params })
        .then((r) => v2.unwrap<Phase>(r));
    },
    create: (data: PhaseWrite): Res<Phase> =>
      api.post(v2.orgUrl('phases/'), data),
    update: (id: string | number, data: Partial<PhaseWrite>): Res<Phase> =>
      api.patch(v2.orgUrl(`phases/${id}/`), data),
    delete: (id: string | number): Res<void> =>
      api.delete(v2.orgUrl(`phases/${id}/`)),
  },

  /** Tasks that repeat on a schedule; the morning job creates each day's task. */
  recurring: {
    list: (projectKey: string): Promise<RecurringTask[]> =>
      api.get(v2.orgUrl('recurring-tasks/'), { params: { project: projectKey, page_size: 200 } })
        .then((r) => v2.unwrap<RecurringTask>(r).data),
    create: (body: RecurringTaskWrite): Promise<RecurringTask> =>
      api.post(v2.orgUrl('recurring-tasks/'), body).then((r) => r.data),
    update: (id: number, body: Partial<RecurringTaskWrite>): Promise<RecurringTask> =>
      api.patch(v2.orgUrl(`recurring-tasks/${id}/`), body).then((r) => r.data),
    remove: (id: number): Promise<void> => api.delete(v2.orgUrl(`recurring-tasks/${id}/`)).then(() => undefined),
  },

  /**
   * Personal API tokens — how a person authenticates a TOOL rather than a browser.
   *
   * Always the caller's own: none of these takes a user id, so nobody can list, create or
   * revoke somebody else's. The secret is returned by `create` and by nothing else, ever.
   */
  apiTokens: {
    list: (): Promise<ApiTokenRow[]> =>
      api.get('/api/auth/api-tokens/').then((r) => r.data),
    create: (name: string, expiresInDays?: number | null): Promise<NewApiToken> =>
      api.post('/api/auth/api-tokens/create/', {
        name,
        ...(expiresInDays ? { expires_in_days: expiresInDays } : {}),
      }).then((r) => r.data),
    revoke: (id: number): Promise<ApiTokenRow> =>
      api.post(`/api/auth/api-tokens/${id}/revoke/`).then((r) => r.data),
  },

  /**
   * The workspace's own priority levels, highest rank first.
   *
   * Priorities are rows, not a fixed enum, so a form that hardcodes their names is wrong for
   * any workspace that does not happen to match. Severities live in the same table under a
   * different `kind` and are filtered out here.
   */
  priorities: {
    list: (): Promise<Array<{ id: number; name: string; rank: number }>> =>
      api.get(v2.orgUrl('priority-levels/?page_size=200'))
        .then((r) => v2.unwrap<Record<string, any>>(r).data
          .filter((row) => String(row.kind || '').toLowerCase() === 'priority')
          .map((row) => ({ id: row.id, name: row.name, rank: row.rank ?? 0 }))
          .sort((a, b) => b.rank - a.rank)),
  },

  //: The conversation on a task. `mentions` are the addresses picked in the composer.
  comments: {
    list: (taskKey: string) => v2.listComments(taskKey),
    post: (taskKey: string, body: string, mentions: string[] = []) => v2.postComment(taskKey, body, mentions),
    edit: (id: number, body: string) => v2.editComment(id, body),
    remove: (id: number) => v2.deleteComment(id),
  },

  documents: {
    list: (projectKey?: string): Res<Document[]> => {
      const params = projectKey ? { project: projectKey } : {};
      return api.get(v2.orgUrl('documents/'), { params })
        .then((r) => v2.unwrap<Document>(r));
    },
    upload: (formData: FormData): Res<Document> =>
      api.post(v2.orgUrl('documents/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    delete: (id: string | number): Res<void> =>
      api.delete(v2.orgUrl(`documents/${id}/`)),
    /** The files attached to one task. */
    forTask: (taskKey: string): Res<Document[]> =>
      api.get(v2.orgUrl(`documents/?work_item=${encodeURIComponent(taskKey)}&page_size=100`))
        .then((r) => v2.unwrap<Document>(r)),
    /** Attach a file to a task. Resolves the project and task keys to ids for the write. */
    attach: (projectKey: string, taskKey: string, file: File): Res<Document> =>
      v2.attachmentFormData(projectKey, taskKey, file).then((form) =>
        api.post(v2.orgUrl('documents/'), form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })),
  },
  subtasks: {
    list: (_projectId: string, taskId: string): Res<Subtask[]> =>
      v2.fetchAllPages(
        `work-items/?parent=${encodeURIComponent(taskId)}&page_size=200`, v2.asSubtask,
      ),
    create: (projectId: string, taskId: string, data: SubtaskWrite): Res<Subtask> =>
      v2.createWorkItem(projectId, data as unknown as TaskWrite, 'task', taskId)
        .then((r) => ({ ...r, data: v2.asSubtask(r.data as never) })) as unknown as Res<Subtask>,
    update: (_projectId: string, _taskId: string, subtaskId: string, data: SubtaskWrite): Res<Subtask> =>
      v2.updateWorkItem(subtaskId, data as unknown as TaskWrite)
        .then((r) => ({ ...r, data: v2.asSubtask(r.data as never) })) as unknown as Res<Subtask>,
    delete: (_projectId: string, _taskId: string, subtaskId: string): Res<void> =>
      api.delete(v2.orgUrl(`work-items/${subtaskId}/`)),
    restore: (_projectId: string, _taskId: string, subtaskId: string): Res<void> =>
      api.post(v2.orgUrl(`work-items/${encodeURIComponent(subtaskId)}/restore/`)),
  },
};

export default api;

/**
 * The money layer, natively typed.
 *
 * Deliberately NOT routed through `v2adapter`: nothing in v1 corresponded to any of this,
 * so there is no legacy shape to translate into and adding one would grow the transitional
 * layer the port is trying to shrink. New surfaces use the generated v2 types directly.
 */
export const financeAPI = {
  portfolio: (params?: { since?: string; until?: string; approved_only?: boolean }): Res<{
    count: number; results: ProjectFinancials[];
  }> => {
    const query = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return api.get(v2.orgUrl(`reports/portfolio/${query ? `?${query}` : ''}`));
  },

  /**
   * Delivery flow: what got finished, how long it took, what is stuck.
   *
   * Server-computed by necessity — the figures need each item's real start and close, and
   * the activity trail exposes only display names over the API, with no date filter and a
   * 50-row page. Manager and above.
   */
  flow: (params?: { project?: string; weeks?: number }): Res<FlowMetrics> => {
    const query = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([k, value]) => [k, String(value)]),
    ).toString();
    return api.get(v2.orgUrl(`reports/flow/${query ? `?${query}` : ''}`));
  },

  /**
   * Each person's delivery record, ranked where the data supports a ranking.
   *
   * Manager and above, like every report — this compares colleagues and is not something
   * the whole workspace reads about each other. The payload carries `limits` and
   * `coverage`, and the caller is expected to render both: a ranking of people shown
   * without what it cannot see is a judgement the data does not support.
   */
  /** `days` is a rolling window; `period` a calendar one (this week / month / year) and wins. */
  contributors: (params?: { project?: string; days?: number; period?: 'this_week' | 'this_month' | 'this_year' }): Res<ContributorReport> => {
    const query = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([k, value]) => [k, String(value)]),
    ).toString();
    return api.get(v2.orgUrl(`reports/contributors/${query ? `?${query}` : ''}`));
  },

  project: (key: string, params?: { since?: string; until?: string }): Res<ProjectFinancials> => {
    const query = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([k, value]) => [k, String(value)]),
    ).toString();
    return api.get(v2.orgUrl(`reports/project/${key}/${query ? `?${query}` : ''}`));
  },

  // The workspace, for its base_currency — every rate must be written in it (the server
  // rejects any other), so the form prefills and locks the currency to this.
  org: (): Res<{ slug: string; name: string; base_currency: string; plan?: string }> =>
    api.get(v2.orgUrl('')),

  rates: {
    list: (): Res<{ count: number; results: BillingRate[] }> =>
      api.get(v2.orgUrl('billing-rates/')),
    create: (body: BillingRateWrite): Res<BillingRate> =>
      api.post(v2.orgUrl('billing-rates/'), body),
    remove: (id: number): Res<void> =>
      api.delete(v2.orgUrl(`billing-rates/${id}/`)),
  },

  budgets: {
    list: (): Res<{ count: number; results: Budget[] }> => api.get(v2.orgUrl('budgets/')),
    create: (body: BudgetWrite): Res<Budget> => api.post(v2.orgUrl('budgets/'), body),
    remove: (id: number): Res<void> => api.delete(v2.orgUrl(`budgets/${id}/`)),
  },

  // The id-carrying option lists that per-project and per-person rate scoping needs. The
  // domain adapters (asProject/asMember) drop the numeric id the write API keys on, so
  // these read the raw v2 rows instead. Members are narrowed to Active, matching the
  // server's write predicate — an Invited row cannot hold a rate.
  scopeOptions: {
    projects: (): Res<{ id: number; key: string; name: string }[]> =>
      api.get(v2.orgUrl('projects/')).then((r) => ({
        ...r,
        data: v2.unwrap<Record<string, any>>(r).data.map((p) => ({
          id: p.id, key: p.key, name: p.name,
        })),
      })),
    members: (): Res<{ id: number; name: string }[]> =>
      api.get(v2.orgUrl('members/')).then((r) => ({
        ...r,
        data: v2.unwrap<Record<string, any>>(r).data
          .filter((m) => m.status === 'Active')
          .map((m) => ({ id: m.id, name: m.display_name || m.invited_email })),
      })),
  },
};

/**
 * The workspace itself, for the Settings screen: its name, its plan and limits, its roster.
 *
 * Native v2 shapes, like `financeAPI`. The Settings screen read the organization's name from
 * a `portal.name` the settings endpoint never returned, so the field always showed the URL
 * slug title-cased — and saving it wrote nothing, because the name lives on the organization
 * and only `PATCH /orgs/<slug>/` changes it.
 */
export type WorkspaceOrg = components['schemas']['Organization'];
export type WorkspaceSettings = components['schemas']['OrgSettings'];

/** One row of the workspace roster as the Members tab needs it. */
export interface RosterMember {
  id: number;
  email: string;
  /** The name they chose, or '' — the screen decides what to show when there is none. */
  name: string;
  role: components['schemas']['WorkspaceRoleEnum'];
  status: components['schemas']['MembershipStatusEnum'];
  /** When they got access, or null when that is not known or they have not joined. */
  joinedAt: string | null;
  /** When they were added to the workspace: the invitation sent, or the row created. */
  addedAt: string | null;
}

export const workspaceAPI = {
  get: (): Res<WorkspaceOrg> => api.get(v2.orgUrl('')),
  /** Owner and Admin only (the server's ADMINISTER tier); the slug never changes with it. */
  rename: (name: string): Res<WorkspaceOrg> => api.patch(v2.orgUrl(''), { name }),
  settings: (): Res<WorkspaceSettings> => api.get(v2.orgUrl('settings/')),
  /** Which roles may use AI tools (the MCP connector and API tokens). Owner and Admin change it. */
  aiAccess: (): Promise<AiAccess> => api.get(v2.orgUrl('settings/ai-access/')).then((r) => r.data),
  setAiAccess: (roles: string[]): Promise<AiAccess> =>
    api.patch(v2.orgUrl('settings/ai-access/'), { roles }).then((r) => r.data),
  /**
   * Every member, with `joined_at`. The shared `projectsAPI.members.list` goes through
   * `asMember`, which carries the row's creation as `joinedAt` — for an invitation, the day
   * it was SENT — so the Members tab read its join dates from the wrong field.
   */
  roster: (): Res<RosterMember[]> =>
    v2.fetchAllPages('members/?page_size=200', (row: components['schemas']['Membership']) => ({
      id: row.id,
      email: row.invited_email,
      name: row.display_name || '',
      role: row.role,
      status: row.status,
      joinedAt: row.joined_at ?? null,
      addedAt: row.created_at ?? null,
    })),
};

/** The current member's own notification preferences for the active workspace. */
export interface EmailPreferences {
  notify_email_on_assignment: boolean;
  /** The one email on the day a task is due. */
  notify_email_on_deadlines: boolean;
  /** A direct @mention in a comment — never every comment. */
  notify_email_on_mention: boolean;
}

export const preferencesAPI = {
  get: (): Res<EmailPreferences> =>
    api.get(v2.orgUrl('me/preferences/')),
  update: (body: Partial<EmailPreferences>): Res<EmailPreferences> =>
    api.patch(v2.orgUrl('me/preferences/'), body),
};

/**
 * The weekly team report. Preview computes it without saving (a Developer gets only their
 * own section); saving freezes the facts plus the author's notes as a snapshot that later
 * task edits do not change. The figures are always computed by the server.
 */
export const weeklyReportAPI = {
  preview: (body: WeeklyReportRequest): Promise<WeeklyReportPayload> =>
    api.post(v2.orgUrl('reports/weekly/preview/'), body).then((r) => r.data.payload),
  save: (body: WeeklyReportSave): Promise<{ id: number; payload: WeeklyReportPayload }> =>
    api.post(v2.orgUrl('reports/weekly/'), body).then((r) => r.data),
  history: (projectKey: string): Promise<WeeklyReportSummary[]> =>
    api.get(v2.orgUrl(`reports/weekly/?project=${encodeURIComponent(projectKey)}`)).then((r) => r.data),
  get: (id: number): Promise<{ id: number; payload: WeeklyReportPayload }> =>
    api.get(v2.orgUrl(`reports/weekly/${id}/`)).then((r) => r.data),
  remove: (id: number): Promise<void> =>
    api.delete(v2.orgUrl(`reports/weekly/${id}/`)).then(() => undefined),
  /** Email a saved report to the addresses given (Owner, Admin or the project's owner). */
  share: (id: number, recipients: string[], note: string): Promise<{ sent_to: string[] }> =>
    api.post(v2.orgUrl(`reports/weekly/${id}/share/`), { recipients, note }).then((r) => r.data),
  /** The saved report as one self-contained HTML file. */
  html: (id: number): Promise<{ blob: Blob; filename: string }> =>
    api.get(v2.orgUrl(`reports/weekly/${id}/html/`), { responseType: 'blob' }).then((r) => {
      const disposition = String(r.headers?.['content-disposition'] || '');
      const match = /filename="([^"]+)"/.exec(disposition);
      return { blob: r.data as Blob, filename: match ? match[1] : `team-report-${id}.html` };
    }),
};

export type WeeklyPreset = 'this_week' | 'last_7_days' | 'last_14_days' | 'custom';

export interface WeeklyReportRequest {
  project?: string;
  goal?: number | null;
  module?: number | null;
  preset: WeeklyPreset;
  start?: string | null;
  end?: string | null;
  /** member id -> [from, to] */
  leave?: Record<string, [string, string]>;
}

export interface WeeklyStep { date: string; step: string; status: 'Done' | 'Next' | 'Waiting' | 'Open' }
export interface WeeklySection { title: string; steps: WeeklyStep[] }

export interface WeeklyReportSave extends WeeklyReportRequest {
  notes?: Record<string, string>;
  statuses?: Record<string, string>;
  sections?: WeeklySection[];
  deployed?: string[];
  direction?: string;
  risks?: string[];
}

export interface WeeklyTask {
  key: string; title: string; status: string; due: string | null; link: string;
  done_on?: string; overdue?: boolean; on_hold?: boolean;
}

export interface WeeklyPerson {
  person: { id: number; name: string; email: string };
  area: string[];
  status: string;
  waiting_on: string;
  on_leave: [string, string] | null;
  counts: { done: number; in_progress: number; overdue: number; hours: number };
  last_activity: string | null;
  delivered: WeeklyTask[];
  sent_back: WeeklyTask[];
  open: WeeklyTask[];
  reviews_given: { approved: number; changes_requested: number; keys: string[] };
  responsiveness: {
    last_comment: string | null; mentions: number; replied_within_1_working_day: number;
    replied_later: number; not_replied: number;
    unanswered: Array<{ key: string; asked: string; working_days_waiting: number }>;
  };
  notes: string;
}

export interface WeeklyKpi { count: number; keys: string[] }

export interface WeeklyReportPayload {
  title: string;
  scope: { label: string; project: string | null; goal: number | null; module: number | null };
  period: { start: string; end: string };
  target: { date: string | null; days_left: number | null; working_days_left: number };
  generated_at: string;
  generated_by: { id: number; name: string; email: string };
  own_section_only: boolean;
  kpis: { completed: WeeklyKpi; in_progress: WeeklyKpi; overdue: WeeklyKpi; due_next_3_days: WeeklyKpi; hours: number };
  people: WeeklyPerson[];
  risks: string[];
  sections: WeeklySection[];
  deployed: string[];
  direction: string;
  footer: string[];
}

export type AiAccess = components['schemas']['AiAccess'];
export type RecurringTask = components['schemas']['RecurringTask'];
export type RecurringTaskWrite = components['schemas']['RecurringTaskWriteRequest'];

export interface WeeklyReportSummary {
  id: number; project: string | null; scope_label: string; period_start: string; period_end: string;
  author: string; created_at: string;
  /** Prepared by the Monday job for the project's owner, not saved by a person. */
  scheduled?: boolean;
  /** Whether the reader may delete it: its author, an Owner, an Admin or the project's owner. */
  can_delete?: boolean;
}
